import React, { useEffect, useState } from 'react';
import { Input } from 'antd';
import { useTranslation } from 'react-i18next';

export interface ZipcodeInputData {
  zipcodes: string[];
}

interface ZipcodeInputProps {
  data: ZipcodeInputData;
  isEditing?: boolean;
  onChange?: (data: ZipcodeInputData) => void;
  placeholder?: string;
  rows?: number;
}

const ZipcodeInput: React.FC<ZipcodeInputProps> = ({
  data,
  isEditing = true,
  onChange,
  placeholder = "Enter zipcodes separated by commas, spaces, or new lines (e.g., 12345, 67890, 54321)",
  rows = 4,
}) => {

  const [internalZipcodes, setInternalZipcodes] = useState<string>("");
  const [userIsTyping, setUserIsTyping] = useState(false);

  // 响应数据变化，更新内部状态
  useEffect(() => {
    if (!userIsTyping) {
      const zipcodesString = data.zipcodes.length > 0 ? data.zipcodes.join(', ') : '';
      setInternalZipcodes(zipcodesString);
    }
  }, [data.zipcodes, userIsTyping]);

  // 解析和验证 zipcode 的函数
  const parseAndValidateZipcodes = (input: string) => {
    // 支持多种分隔符：逗号、空格、换行、分号、制表符
    const rawZipcodes = input
      .split(/[,;\s\n\r\t]+/)
      .map(code => code.trim())
      .filter(code => code.length > 0);

    // 验证每个 zipcode
    const validZipcodes: string[] = [];
    const invalidZipcodes: string[] = [];
    const duplicates: string[] = [];

    rawZipcodes.forEach(code => {
      // zipcode 格式验证：只允许数字
      const isValidFormat = /^\d+$/.test(code);

      if (!isValidFormat) {
        invalidZipcodes.push(code);
      } else if (validZipcodes.includes(code)) {
        if (!duplicates.includes(code)) {
          duplicates.push(code);
        }
      } else {
        validZipcodes.push(code);
      }
    });

    return {
      validZipcodes,
      invalidZipcodes,
      duplicates,
      totalCount: rawZipcodes.length
    };
  };

  const handleChange = (value: string) => {
    setUserIsTyping(true);
    setInternalZipcodes(value);
    
    // 实时验证并通知父组件
    const { validZipcodes, invalidZipcodes, duplicates } = parseAndValidateZipcodes(value);
    
    // 只有当没有无效数据或重复数据时才更新
    if (invalidZipcodes.length === 0 && duplicates.length === 0) {
      onChange?.({ zipcodes: validZipcodes });
    }
  };

  const handleFocus = () => {
    setUserIsTyping(true);
  };

  const handleBlur = () => {
    // 失焦后立即重置状态，减少闪烁
    setUserIsTyping(false);
  };

  // 计算统计信息
  const {
    validZipcodes,
    invalidZipcodes,
    duplicates,
    totalCount
  } = parseAndValidateZipcodes(internalZipcodes);
  const {t} = useTranslation();
  return (
    <>
      <Input.TextArea
        value={internalZipcodes}
        rows={rows}
        readOnly={!isEditing}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={isEditing ? placeholder : ""}
        className="w-full"
        disabled={!isEditing}
      />
      <div className="mt-2 space-y-1">
        <div className="text-gray-500">
          {t('pickup.pickupTask.pageDetails.columns.total')}: {totalCount} | {t('pickup.pickupTask.pageDetails.columns.valid')}: {validZipcodes.length}
        </div>

        {duplicates.length > 0 && (
          <div className="text-orange-500 text-sm"> 
            {duplicates.length} {t('pickup.pickupTask.pageDetails.columns.duplicate')} zipcode{duplicates.length > 1 ? 's' : ''}: {duplicates.join(', ')}
          </div>
        )}

        {invalidZipcodes.length > 0 && (
          <div className="text-red-500 text-sm">
            {invalidZipcodes.length} {t('pickup.pickupTask.pageDetails.columns.invalid')} zipcode{invalidZipcodes.length > 1 ? 's' : ''}: {invalidZipcodes.slice(0, 3).join(', ')}{invalidZipcodes.length > 3 ? '...' : ''}
            <div className="text-xs mt-1">
              {t('pickup.pickupTask.pageDetails.messages.zipcodesMustBeNumbersOnly')}
            </div>
          </div>
        )}

        {(invalidZipcodes.length > 0 || duplicates.length > 0) && (
          <div className="text-red-500 text-sm font-medium">
             {t('pickup.pickupTask.pageDetails.messages.cannotSave')}
          </div>
        )}
      </div>
    </>
  );
};

export default ZipcodeInput;
